import { useState, useEffect } from 'react';
import type { CodeToHtmlOptions } from '@llm-ui/code';
import {
  loadHighlighter,
  useCodeBlockToHtml,
  allLangs,
  allLangsAlias,
} from '@llm-ui/code';
import { type LLMOutputComponent } from '@llm-ui/react';
import { getHighlighterCore } from 'shiki/core';
// WARNING: Importing bundledLanguagesInfo will increase your bundle size
// See: https://llm-ui.com/docs/blocks/code#bundle-size
import { bundledThemes } from 'shiki/themes';
import { bundledLanguagesInfo } from 'shiki/langs';
import getWasm from 'shiki/wasm';
import './code-blocks.css';
import { extractCode } from '@/lib/markdown-matchers';
import HtmlCodeBlock from './HtmlCodeBlock';
import {
  getDisplayLanguage,
  preProcessCodeBlock,
} from '@/lib/markdown-code-utils';
import FormattedTextCodeBlock from './FormattedTextCodeBlock';

const langAlias = allLangsAlias(bundledLanguagesInfo);
langAlias['node'] = 'js';
langAlias['curl'] = 'bash';
langAlias['dotnet'] = 'csharp';

const highlighter = loadHighlighter(
  getHighlighterCore({
    langs: allLangs(bundledLanguagesInfo),
    langAlias,
    themes: Object.values(bundledThemes),
    loadWasm: getWasm,
  })
);

const codeToHtmlOptions: CodeToHtmlOptions = {
  theme: 'github-dark',
};

export const CodeBlock: LLMOutputComponent = ({ blockMatch }) => {
  const [normalizedCode, setNormalizedCode] = useState('');
  const [normalizedLanguage, setNormalizedLanguage] = useState('');

  useEffect(() => {
    const codeBlockContent = blockMatch.output || '';

    const { code, language } = extractCode(
      preProcessCodeBlock(codeBlockContent)
    );

    setNormalizedCode(code);
    setNormalizedLanguage(language);
  }, [blockMatch.output]);

  // Use the llm-ui library to convert markdown code block to HTML
  const { html, code } = useCodeBlockToHtml({
    markdownCodeBlock: blockMatch.output,
    highlighter,
    codeToHtmlOptions,
  });

  const displayLanguage = getDisplayLanguage(normalizedLanguage || '');

  const displayText = normalizedCode || code;

  // Fallback rendering when HTML generation fails
  if (!html && displayText) {
    return (
      <FormattedTextCodeBlock
        language={displayLanguage}
        content={displayText}
      />
    );
  }

  return (
    <HtmlCodeBlock
      language={displayLanguage}
      htmlContent={html}
      fallbackText={displayText}
    />
  );
};

export default CodeBlock;
